import moment from 'moment-timezone'
import { useMemo } from 'react'

import { DISPLAY_STATUS } from 'components/meetings/utils'
import { useCurrentTime } from 'shared/contexts'

/**
 * Status object returned by the hook (displayText is computed in the component layer via computeStatusDisplayText)
 */
export interface MeetingStatusForDetail {
  status: string
  startTime: string | undefined
}

/**
 * Optional status calculation functions that can be provided via dependencies
 */
export interface StatusCalculationDependencies {
  getMeetingStatus?: (meeting: any, currentTime?: moment.Moment) => string
  getUpNextMeetingId?: (meetings: any[], currentTime?: moment.Moment) => string | null
}

/**
 * Parameters for useMeetingStatusForDetail hook
 */
export interface UseMeetingStatusForDetailParams {
  event: {
    uuid?: string
    startTime?: string
    endTime?: string
    [key: string]: unknown
  } | null | undefined
  todayMeetings?: Array<{
    uuid?: string
    startTime?: string
    endTime?: string
    [key: string]: unknown
  }> | null
  dependencies?: StatusCalculationDependencies
}

/**
 * Hook for calculating meeting status for detail view.
 * Uses browser time directly for status calculations, ensuring timezone consistency.
 * Status recalculates on every render when dependencies change.
 *
 * @param params - Hook parameters
 * @param params.event - The meeting event object
 * @param params.todayMeetings - Array of meetings for the same day (for "up next" detection)
 * @param params.dependencies - MeetingDetailDependencies object containing optional status functions
 * @returns Status object with { status, startTime } or null if not applicable
 */
export const useMeetingStatusForDetail = ({
  event,
  todayMeetings,
  dependencies,
}: UseMeetingStatusForDetailParams): MeetingStatusForDetail | null => {
  const { getMeetingStatus, getUpNextMeetingId } = dependencies || {}

  // Use shared current time to keep status displays in sync across list and right rail
  const currentTime = useCurrentTime()

  // Check if this is a realtime meeting by checking if it has uuid and required fields
  const isRealtimeMeeting = Boolean(event?.uuid && event?.startTime && event?.endTime)

  const meetingStatus = useMemo(() => {
    if (!isRealtimeMeeting || !getMeetingStatus) return null

    let isUpNext = false
    if (todayMeetings && Array.isArray(todayMeetings) && getUpNextMeetingId) {
      const upNextMeetingId = getUpNextMeetingId(todayMeetings, currentTime)
      isUpNext = upNextMeetingId === event?.uuid
    }

    const status = getMeetingStatus(event, currentTime)
    const finalStatus = isUpNext && status === DISPLAY_STATUS.UPCOMING ? DISPLAY_STATUS.UP_NEXT : status

    return {
      status: finalStatus,
      startTime: event?.startTime,
    }
  }, [isRealtimeMeeting, event, todayMeetings, getMeetingStatus, getUpNextMeetingId, currentTime])

  return meetingStatus
}
